import React, { useState, useEffect } from "react";
import { FaWhatsapp } from "react-icons/fa";

import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import {
  fetchAdminUpdates,
  createUpdate,
  updateUpdate,
  deleteUpdate,
  fetchProjects,
  fetchUsers,
  uploadUpdateImage,
  fetchUpdateReactionDetails,
  fetchUpdateCategories,
} from "../api/api";

const CUSTOMER_ROLE_ID = 4;

// Same "contact me" reactions as the customer-facing feed (see
// CustomerUpdatesFeed.js) — worth calling out distinctly here since these
// are the ones an admin actually needs to act on.
const CONTACT_EMOJI = "📞";
const WHATSAPP_EMOJI = "📲";

function reactionCounts(reactions) {
  const counts = {};
  for (const r of reactions || []) {
    counts[r.emoji] = (counts[r.emoji] || 0) + 1;
  }
  return counts;
}

// Indian mobile numbers are stored bare, 10 digits, no country code (see
// countryCodes.js's combineMobile — +91 is the one country code that keeps
// the old bare-digit format for backward compatibility). Any other country
// stores <country code digits><local number> concatenated, so a stored
// number longer than 10 digits already has its country code on the front.
function withCountryCode(mobile) {
  const digits = (mobile || "").replace(/\D/g, "");
  return digits.length === 10 ? `91${digits}` : digits;
}
function telHref(mobile) {
  return `tel:+${withCountryCode(mobile)}`;
}
function whatsappHref(mobile) {
  return `https://wa.me/${withCountryCode(mobile)}`;
}
function displayMobile(mobile) {
  return `+${withCountryCode(mobile)}`;
}

const EMPTY_FORM = {
  title: "",
  body: "",
  category_id: "",
  image_url: "",
  farm_name: "Jain Farms Bagalur",
  location: "",
  published: true,
  // "" means farm-wide (every customer) — matches the backend's project_id
  // being null. Set to a Project id to scope this update to only
  // customers who own a plot in that project (see GET /updates/mine).
  project_id: "",
  // Explicit per-customer targeting, on top of (or instead of) project_id —
  // an update is "specific to a customer" in GET /updates/mine if either
  // this list includes them or project_id matches a project they own a
  // plot in. Holds user ids; see CustomerPicker for how names are resolved.
  recipient_user_ids: [],
};

// Search-and-pick UI for targeting specific customers. `allCustomers` is
// fetched once (see EstateUpdates below) rather than searched live per
// keystroke — simpler, and it doubles as the lookup for showing names next
// to already-selected ids when editing an existing update.
function CustomerPicker({ allCustomers, selectedIds, onChange }) {
  const [query, setQuery] = useState("");

  const matches = query.trim()
    ? allCustomers
        .filter((c) => !selectedIds.includes(c.id))
        .filter((c) => {
          const q = query.trim().toLowerCase();
          return c.full_name?.toLowerCase().includes(q) || c.mobile?.includes(q);
        })
        .slice(0, 8)
    : [];

  const add = (id) => {
    onChange([...selectedIds, id]);
    setQuery("");
  };
  const remove = (id) => onChange(selectedIds.filter((sid) => sid !== id));

  return (
    <div style={{ marginBottom: "10px" }}>
      <label style={{ display: "block", marginBottom: "4px", fontSize: "13px", color: "#555" }}>
        Specific customers (optional — leave empty to not target individuals)
      </label>
      <input
        type="text"
        placeholder="Search customer by name or mobile..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {matches.length > 0 && (
        <div style={{ border: "1px solid #ddd", borderRadius: "6px", marginTop: "4px" }}>
          {matches.map((c) => (
            <div
              key={c.id}
              onClick={() => add(c.id)}
              style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid #eee" }}
            >
              {c.full_name} — {c.mobile}
            </div>
          ))}
        </div>
      )}
      {selectedIds.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
          {selectedIds.map((id) => {
            const c = allCustomers.find((cust) => cust.id === id);
            return (
              <span key={id} style={{ background: "#e5e7eb", borderRadius: "14px", padding: "4px 10px", fontSize: "13px" }}>
                {c ? c.full_name : `Customer #${id}`}{" "}
                <button
                  type="button"
                  onClick={() => remove(id)}
                  style={{ border: "none", background: "none", cursor: "pointer", color: "#dc2626", fontWeight: "bold" }}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function errorMessage(err) {
  const detail = err.response?.data?.detail;
  return detail || "Something went wrong. Please try again.";
}

function EstateUpdates() {

  const [updates, setUpdates] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(true);
  const [updatesError, setUpdatesError] = useState("");

  const [projects, setProjects] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  // Admin-curated master list (see app/models/update_category.py) — replaces
  // the old hardcoded CATEGORIES array, so adding a category is just a new
  // row, not a code change in three repos.
  const [categories, setCategories] = useState([]);

  // Mirrors the targeting fields on the create/edit form: filter the
  // currently-loaded list down to updates that match. "" means "All" for
  // both. audience is either "" (all), "all" (farm-wide only), a project id
  // (only that project's targeted updates), or "customer" (paired with
  // filterCustomerId to match a specific recipient).
  const [filterCategory, setFilterCategory] = useState("");
  const [filterAudience, setFilterAudience] = useState("");
  const [filterCustomerId, setFilterCustomerId] = useState("");
  const [customerFilterQuery, setCustomerFilterQuery] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [rowBusy, setRowBusy] = useState(null);
  const [rowError, setRowError] = useState("");

  const [imageUploading, setImageUploading] = useState(false);
  const [editImageUploading, setEditImageUploading] = useState(false);

  // Which update's "who reacted" panel is open, keyed by update id — plus
  // a per-update cache of the fetched reactor details (name/mobile/plot
  // numbers) so reopening a panel doesn't re-fetch.
  const [expandedReactionsId, setExpandedReactionsId] = useState(null);
  const [reactionDetails, setReactionDetails] = useState({});
  const [reactionDetailsLoading, setReactionDetailsLoading] = useState(null);
  const [reactionDetailsError, setReactionDetailsError] = useState("");
  // Facebook-style "All / 👍 8 / ❤️ 3 / ..." tabs within the open panel —
  // one shared filter is fine since only one update's panel is ever open
  // at a time (see expandedReactionsId).
  const [reactionFilter, setReactionFilter] = useState("all");

  const toggleReactionDetails = async (updateId) => {
    if (expandedReactionsId === updateId) {
      setExpandedReactionsId(null);
      return;
    }
    setExpandedReactionsId(updateId);
    setReactionFilter("all");
    if (reactionDetails[updateId]) return;

    setReactionDetailsLoading(updateId);
    setReactionDetailsError("");
    try {
      const data = await fetchUpdateReactionDetails(updateId);
      setReactionDetails((prev) => ({ ...prev, [updateId]: data }));
    } catch (err) {
      setReactionDetailsError(errorMessage(err));
    } finally {
      setReactionDetailsLoading(null);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    setFormError("");
    try {
      const image_url = await uploadUpdateImage(file);
      setForm((prev) => ({ ...prev, image_url }));
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setImageUploading(false);
      e.target.value = "";
    }
  };

  const handleEditImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditImageUploading(true);
    setRowError("");
    try {
      const image_url = await uploadUpdateImage(file);
      setEditForm((prev) => ({ ...prev, image_url }));
    } catch (err) {
      setRowError(errorMessage(err));
    } finally {
      setEditImageUploading(false);
      e.target.value = "";
    }
  };

  const loadUpdates = async (category) => {
    setUpdatesLoading(true);
    setUpdatesError("");
    try {
      const data = await fetchAdminUpdates(category || undefined);
      setUpdates(data);
    } catch (err) {
      setUpdatesError(errorMessage(err));
    } finally {
      setUpdatesLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects().then((data) => setProjects(data.filter((p) => p.is_active))).catch(() => {});
    fetchUsers({ roleId: CUSTOMER_ROLE_ID, limit: 1000 }).then(setAllCustomers).catch(() => {});
    fetchUpdateCategories().then((data) => setCategories(data.filter((c) => c.is_active))).catch(() => {});
  }, []);

  useEffect(() => {
    loadUpdates(filterCategory);
  }, [filterCategory]);

  const audienceFilteredUpdates = updates.filter((update) => {
    if (filterAudience === "all") {
      return !update.project_id && (!update.recipient_user_ids || update.recipient_user_ids.length === 0);
    }
    if (filterAudience === "customer") {
      return filterCustomerId ? (update.recipient_user_ids || []).includes(Number(filterCustomerId)) : true;
    }
    if (filterAudience) {
      // A project id was selected.
      return String(update.project_id || "") === filterAudience;
    }
    return true;
  });

  const customerFilterMatches = customerFilterQuery.trim()
    ? allCustomers
        .filter((c) => {
          const q = customerFilterQuery.trim().toLowerCase();
          return c.full_name?.toLowerCase().includes(q) || c.mobile?.includes(q);
        })
        .slice(0, 8)
    : [];
  const selectedFilterCustomer = allCustomers.find((c) => c.id === Number(filterCustomerId));

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleCreateUpdate = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.title.trim() || !form.body.trim() || !form.farm_name.trim() || !form.category_id) {
      setFormError("Title, Body, Category, and Farm Name are required.");
      return;
    }

    setSubmitting(true);
    try {
      await createUpdate({
        title: form.title.trim(),
        body: form.body.trim(),
        category_id: Number(form.category_id),
        image_url: form.image_url.trim() || undefined,
        farm_name: form.farm_name.trim(),
        location: form.location.trim() || undefined,
        published: form.published,
        project_id: form.project_id ? Number(form.project_id) : null,
        recipient_user_ids: form.recipient_user_ids,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadUpdates(filterCategory);
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (update) => {
    setEditingId(update.id);
    setEditForm({
      title: update.title,
      body: update.body,
      category_id: update.category_id || "",
      image_url: update.image_url || "",
      farm_name: update.farm_name,
      location: update.location || "",
      published: update.published !== undefined ? update.published : true,
      project_id: update.project_id || "",
      recipient_user_ids: update.recipient_user_ids || [],
    });
    setRowError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const saveEdit = async (updateId) => {
    if (!editForm.title.trim() || !editForm.body.trim()) {
      setRowError("Title and Body are required.");
      return;
    }

    setRowBusy(updateId);
    setRowError("");
    try {
      const updated = await updateUpdate(updateId, {
        title: editForm.title.trim(),
        body: editForm.body.trim(),
        category_id: editForm.category_id ? Number(editForm.category_id) : null,
        image_url: editForm.image_url.trim() || null,
        farm_name: editForm.farm_name.trim(),
        location: editForm.location.trim() || null,
        published: editForm.published,
        project_id: editForm.project_id ? Number(editForm.project_id) : null,
        recipient_user_ids: editForm.recipient_user_ids,
      });
      setUpdates((prev) => prev.map((u) => (u.id === updateId ? updated : u)));
      setEditingId(null);
    } catch (err) {
      setRowError(errorMessage(err));
    } finally {
      setRowBusy(null);
    }
  };

  const handleDelete = async (update) => {
    if (!window.confirm(`Delete update "${update.title}"? This also removes its reactions and comments.`)) {
      return;
    }

    setRowBusy(update.id);
    setRowError("");
    try {
      await deleteUpdate(update.id);
      setUpdates((prev) => prev.filter((u) => u.id !== update.id));
    } catch (err) {
      setRowError(errorMessage(err));
    } finally {
      setRowBusy(null);
    }
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h1 style={{ marginTop: "0", marginBottom: 0 }}>Estate Updates</h1>
          <button className="login-btn" style={{ width: "auto", padding: "10px 20px" }} onClick={() => setShowForm((prev) => !prev)}>
            {showForm ? "Cancel" : "+ New Update"}
          </button>
        </div>

        {updatesError && <p className="login-error" style={{ color: "red" }}>{updatesError}</p>}
        {rowError && <p className="login-error" style={{ color: "red" }}>{rowError}</p>}

        <div className="section-card" style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "13px", color: "#555" }}>Category</label>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="">All categories</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "13px", color: "#555" }}>Audience</label>
            <select
              value={filterAudience}
              onChange={(e) => {
                setFilterAudience(e.target.value);
                setFilterCustomerId("");
                setCustomerFilterQuery("");
              }}
            >
              <option value="">All</option>
              <option value="all">All customers (farm-wide)</option>
              {projects.map((p) => <option key={p.id} value={String(p.id)}>Only {p.name} owners</option>)}
              <option value="customer">Specific customer</option>
            </select>
          </div>

          {filterAudience === "customer" && (
            <div style={{ position: "relative" }}>
              <label style={{ display: "block", marginBottom: "4px", fontSize: "13px", color: "#555" }}>Customer</label>
              {selectedFilterCustomer ? (
                <span style={{ background: "#e5e7eb", borderRadius: "14px", padding: "4px 10px", fontSize: "13px" }}>
                  {selectedFilterCustomer.full_name}{" "}
                  <button
                    type="button"
                    onClick={() => setFilterCustomerId("")}
                    style={{ border: "none", background: "none", cursor: "pointer", color: "#dc2626", fontWeight: "bold" }}
                  >
                    ×
                  </button>
                </span>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Search customer by name or mobile..."
                    value={customerFilterQuery}
                    onChange={(e) => setCustomerFilterQuery(e.target.value)}
                  />
                  {customerFilterMatches.length > 0 && (
                    <div style={{ position: "absolute", zIndex: 1, background: "#fff", border: "1px solid #ddd", borderRadius: "6px", marginTop: "4px", minWidth: "220px" }}>
                      {customerFilterMatches.map((c) => (
                        <div
                          key={c.id}
                          onClick={() => { setFilterCustomerId(String(c.id)); setCustomerFilterQuery(""); }}
                          style={{ padding: "6px 10px", cursor: "pointer", borderBottom: "1px solid #eee" }}
                        >
                          {c.full_name} — {c.mobile}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {(filterCategory || filterAudience) && (
            <button
              type="button"
              className="register-btn"
              style={{ width: "auto", padding: "8px 16px" }}
              onClick={() => { setFilterCategory(""); setFilterAudience(""); setFilterCustomerId(""); setCustomerFilterQuery(""); }}
            >
              Clear filters
            </button>
          )}
        </div>

        {showForm && (
          <div className="section-card">
            <h3>New Update</h3>

            {formError && <p className="login-error" style={{ color: "red" }}>{formError}</p>}

            <form onSubmit={handleCreateUpdate}>
              <input type="text" name="title" placeholder="Title *" value={form.title} onChange={handleFormChange} required />

              <select name="category_id" value={form.category_id} onChange={handleFormChange} required>
                <option value="">Select a category *</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>

              <textarea rows="4" name="body" placeholder="Body *" value={form.body} onChange={handleFormChange} required />

              <label style={{ display: "block", marginTop: "8px", marginBottom: "4px", fontSize: "13px", color: "#555" }}>
                Image (optional)
              </label>
              <input type="file" accept="image/*" onChange={handleImageSelect} disabled={imageUploading} />
              {imageUploading && <p style={{ fontSize: "13px", color: "#555" }}>Uploading…</p>}
              {form.image_url && !imageUploading && (
                <div style={{ marginTop: "6px" }}>
                  <img src={form.image_url} alt="Update" style={{ maxWidth: "160px", maxHeight: "160px", borderRadius: "6px", display: "block" }} />
                  <button
                    type="button"
                    className="view-btn"
                    style={{ marginTop: "4px" }}
                    onClick={() => setForm((prev) => ({ ...prev, image_url: "" }))}
                  >
                    Remove image
                  </button>
                </div>
              )}
              <input type="text" name="farm_name" placeholder="Farm Name *" value={form.farm_name} onChange={handleFormChange} required />
              <input type="text" name="location" placeholder="Location (optional)" value={form.location} onChange={handleFormChange} />

              <select name="project_id" value={form.project_id} onChange={handleFormChange}>
                <option value="">All customers (farm-wide)</option>
                {projects.map((p) => <option key={p.id} value={p.id}>Only {p.name} owners</option>)}
              </select>

              <CustomerPicker
                allCustomers={allCustomers}
                selectedIds={form.recipient_user_ids}
                onChange={(ids) => setForm((prev) => ({ ...prev, recipient_user_ids: ids }))}
              />

              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input type="checkbox" name="published" checked={form.published} onChange={handleFormChange} />
                Published (visible on the app's home screen immediately)
              </label>

              <button type="submit" className="login-btn" style={{ width: "auto", padding: "8px 16px" }} disabled={submitting || imageUploading}>
                {submitting ? "Posting…" : "Post Update"}
              </button>
            </form>
          </div>
        )}

        {updatesLoading && <p>Loading updates…</p>}

        {!updatesLoading && (
          <div className="updates-list">

            {audienceFilteredUpdates.length === 0 && (
              <p>{updates.length === 0 ? "No updates yet." : "No updates match the selected filters."}</p>
            )}

            {audienceFilteredUpdates.map((update) => {
              const counts = reactionCounts(update.reactions);
              const totalReactions = update.reactions?.length || 0;
              return (
              <div className="update-card" key={update.id}>

                {editingId === update.id ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <input type="text" name="title" value={editForm.title} onChange={handleEditFormChange} />

                    <select name="category_id" value={editForm.category_id} onChange={handleEditFormChange} required>
                      <option value="">Select a category *</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>

                    <textarea rows="4" name="body" value={editForm.body} onChange={handleEditFormChange} />

                    <label style={{ fontSize: "13px", color: "#555" }}>Image (optional)</label>
                    <input type="file" accept="image/*" onChange={handleEditImageSelect} disabled={editImageUploading} />
                    {editImageUploading && <p style={{ fontSize: "13px", color: "#555" }}>Uploading…</p>}
                    {editForm.image_url && !editImageUploading && (
                      <div>
                        <img src={editForm.image_url} alt="Update" style={{ maxWidth: "160px", maxHeight: "160px", borderRadius: "6px", display: "block" }} />
                        <button
                          type="button"
                          className="view-btn"
                          style={{ marginTop: "4px" }}
                          onClick={() => setEditForm((prev) => ({ ...prev, image_url: "" }))}
                        >
                          Remove image
                        </button>
                      </div>
                    )}

                    <input type="text" name="farm_name" value={editForm.farm_name} onChange={handleEditFormChange} />
                    <input type="text" name="location" placeholder="Location" value={editForm.location} onChange={handleEditFormChange} />

                    <select name="project_id" value={editForm.project_id} onChange={handleEditFormChange}>
                      <option value="">All customers (farm-wide)</option>
                      {projects.map((p) => <option key={p.id} value={p.id}>Only {p.name} owners</option>)}
                    </select>

                    <CustomerPicker
                      allCustomers={allCustomers}
                      selectedIds={editForm.recipient_user_ids}
                      onChange={(ids) => setEditForm((prev) => ({ ...prev, recipient_user_ids: ids }))}
                    />

                    <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input type="checkbox" name="published" checked={editForm.published} onChange={handleEditFormChange} />
                      Published
                    </label>

                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        type="button"
                        className="login-btn"
                        style={{ width: "auto", padding: "6px 14px" }}
                        onClick={() => saveEdit(update.id)}
                        disabled={rowBusy === update.id || editImageUploading}
                      >
                        {rowBusy === update.id ? "Saving…" : "Save"}
                      </button>
                      <button
                        type="button"
                        className="register-btn"
                        style={{ width: "auto", padding: "6px 14px" }}
                        onClick={cancelEdit}
                        disabled={rowBusy === update.id}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="update-top">
                      <div>
                        <h4>{update.title}</h4>
                        <p>
                          {update.category_name}
                          {update.recipient_user_ids?.length > 0
                            ? ` · ${update.recipient_user_ids.length} specific customer${update.recipient_user_ids.length > 1 ? "s" : ""}`
                            : update.project_name
                              ? ` · Only ${update.project_name} owners`
                              : " · All customers"}
                          {!update.published && " · Draft"}
                        </p>
                      </div>
                    </div>

                    {update.image_url && (
                      <img src={update.image_url} alt="" style={{ maxWidth: "220px", maxHeight: "160px", borderRadius: "6px", marginBottom: "8px" }} />
                    )}

                    <div className="update-body">{update.body}</div>

                    <div className="update-footer">
                      <span>{update.farm_name}{update.location ? ` — ${update.location}` : ""}</span>
                      <span>{new Date(update.created_at).toLocaleDateString()}</span>
                    </div>

                    {totalReactions > 0 && (
                      <div style={{ marginTop: "8px" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                          {Object.entries(counts).map(([emoji, count]) => (
                            <span
                              key={emoji}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "13px",
                                padding: "3px 9px",
                                borderRadius: "14px",
                                background: emoji === CONTACT_EMOJI || emoji === WHATSAPP_EMOJI ? "#fef3c7" : "#f3f4f6",
                                fontWeight: emoji === CONTACT_EMOJI || emoji === WHATSAPP_EMOJI ? 700 : 400,
                              }}
                            >
                              {emoji === WHATSAPP_EMOJI ? <FaWhatsapp color="#25D366" /> : emoji} {count}
                            </span>
                          ))}
                          <button
                            type="button"
                            className="view-btn"
                            style={{ fontSize: "13px", padding: "3px 10px" }}
                            onClick={() => toggleReactionDetails(update.id)}
                          >
                            {expandedReactionsId === update.id ? "Hide" : "Who reacted?"}
                          </button>
                        </div>

                        {expandedReactionsId === update.id && (
                          <div style={{ marginTop: "8px", border: "1px solid #eee", borderRadius: "8px", padding: "10px" }}>
                            {reactionDetailsLoading === update.id && <p style={{ margin: 0 }}>Loading…</p>}
                            {reactionDetailsError && <p className="login-error" style={{ color: "red", margin: 0 }}>{reactionDetailsError}</p>}

                            {/* Facebook-style "All / 👍 8 / ❤️ 3 / ..." filter tabs */}
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "8px", borderBottom: "1px solid #eee", paddingBottom: "8px" }}>
                              <button
                                type="button"
                                onClick={() => setReactionFilter("all")}
                                style={{
                                  border: "none",
                                  background: reactionFilter === "all" ? "#1B4D2E" : "#f3f4f6",
                                  color: reactionFilter === "all" ? "#fff" : "#333",
                                  borderRadius: "14px",
                                  padding: "4px 12px",
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                }}
                              >
                                All {totalReactions}
                              </button>
                              {Object.entries(counts).map(([emoji, count]) => (
                                <button
                                  type="button"
                                  key={emoji}
                                  onClick={() => setReactionFilter(emoji)}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    border: "none",
                                    background: reactionFilter === emoji ? "#1B4D2E" : "#f3f4f6",
                                    color: reactionFilter === emoji ? "#fff" : "#333",
                                    borderRadius: "14px",
                                    padding: "4px 12px",
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                  }}
                                >
                                  {emoji === WHATSAPP_EMOJI ? (
                                    <FaWhatsapp color={reactionFilter === emoji ? "#fff" : "#25D366"} />
                                  ) : (
                                    emoji
                                  )}{" "}
                                  {count}
                                </button>
                              ))}
                            </div>

                            {(reactionDetails[update.id] || [])
                              .filter((r) => reactionFilter === "all" || r.emoji === reactionFilter)
                              .map((r, i) => {
                              const isContact = r.emoji === CONTACT_EMOJI;
                              const isWhatsapp = r.emoji === WHATSAPP_EMOJI;
                              return (
                                <div
                                  key={`${r.user_id}-${r.emoji}-${i}`}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "6px 0",
                                    borderBottom: "1px solid #f5f5f5",
                                  }}
                                >
                                  <div>
                                    <span style={{ marginRight: "8px" }}>
                                      {isWhatsapp ? <FaWhatsapp color="#25D366" style={{ verticalAlign: "middle" }} /> : r.emoji}
                                    </span>
                                    <strong>{r.full_name}</strong>
                                    {r.plot_numbers.length > 0 && (
                                      <span style={{ color: "#888", fontSize: "13px" }}>
                                        {" "}— Plot {r.plot_numbers.join(", ")}
                                      </span>
                                    )}
                                  </div>
                                  {(isContact || isWhatsapp) && r.mobile && (
                                    <a
                                      href={isWhatsapp ? whatsappHref(r.mobile) : telHref(r.mobile)}
                                      target={isWhatsapp ? "_blank" : undefined}
                                      rel={isWhatsapp ? "noopener noreferrer" : undefined}
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        color: isWhatsapp ? "#25D366" : "#1B4D2E",
                                        textDecoration: "none",
                                      }}
                                    >
                                      {isWhatsapp ? "WhatsApp" : "Call"} {displayMobile(r.mobile)}
                                    </a>
                                  )}
                                </div>
                              );
                            })}

                            {reactionDetailsLoading !== update.id &&
                              !reactionDetailsError &&
                              (reactionDetails[update.id] || []).filter((r) => reactionFilter === "all" || r.emoji === reactionFilter).length === 0 && (
                              <p style={{ margin: 0, color: "#888" }}>
                                {reactionFilter === "all" ? "No reactions yet." : "No one reacted with this."}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                      <button className="view-btn" onClick={() => startEdit(update)} disabled={rowBusy === update.id}>
                        Edit
                      </button>
                      <button
                        className="view-btn"
                        onClick={() => handleDelete(update)}
                        disabled={rowBusy === update.id}
                        style={{ background: "#dc2626", color: "white" }}
                      >
                        {rowBusy === update.id ? "…" : "Delete"}
                      </button>
                    </div>
                  </>
                )}

              </div>
              );
            })}

          </div>
        )}

      </div>

    </div>

  );

}

export default EstateUpdates;
